/**
 * Call Service — centralized phone call routing
 * 
 * Routes phone calls through Teams PSTN deep links or native tel: protocol
 * based on user preference. Default: Teams (for Teams Telephony users).
 * 
 * Teams PSTN deep link format:
 *   https://teams.microsoft.com/l/call/0/0?users=4:<E.164 number>
 *   The "4:" prefix is the PSTN MRI (Media Resource Identifier).
 */

import { ref } from 'vue';
import { branding } from './oauth2-config';

const CALL_MODE_KEY = `${branding.storagePrefix}_call_mode`;

// 'teams' | 'native'
const callMode = ref(localStorage.getItem(CALL_MODE_KEY) || 'teams');

/**
 * Get current call mode
 * @returns {'teams'|'native'}
 */
export function getCallMode() {
  return callMode.value;
}

/**
 * Set call mode and persist
 * @param {'teams'|'native'} mode
 */
export function setCallMode(mode) {
  callMode.value = mode;
  localStorage.setItem(CALL_MODE_KEY, mode);
}

/** Reactive ref for use in Vue templates */
export { callMode };

/**
 * Normalize a phone number to E.164-like format for Teams PSTN MRI.
 * - Strips whitespace, dashes, parentheses, dots, slashes
 * - Keeps leading +
 * - Converts 00xx → +xx (international prefix)
 * - Converts 0xxx → +49xxx (German national → international)
 * @param {string} phone - Raw phone number
 * @returns {string} Normalized number starting with +
 */
export function normalizeForTeams(phone) {
  if (!phone) return '';

  // Strip everything except digits and leading +
  let cleaned = phone.replace(/[^\d+]/g, '');

  // 00xx international prefix → +xx
  if (cleaned.startsWith('00')) {
    cleaned = '+' + cleaned.substring(2);
  }
  // 0xx German national → +49xx
  else if (cleaned.startsWith('0')) {
    cleaned = '+49' + cleaned.substring(1);
  }
  // Already has + → keep as-is
  else if (cleaned.startsWith('+')) {
    // good
  }
  // Raw digits without prefix → assume German
  else if (cleaned.length > 0) {
    cleaned = '+49' + cleaned;
  }

  return cleaned;
}

/**
 * Initiate a phone call using the user's preferred mode.
 * - Teams mode: opens Teams PSTN deep link (works on all devices)
 * - Native mode: triggers tel: protocol (OS default dialer)
 * @param {string} number - Phone number in any format
 */
export function makePhoneCall(number) {
  if (!number) return;

  if (callMode.value === 'teams') {
    const normalized = normalizeForTeams(number);
    if (!normalized) return;
    window.open(
      `https://teams.microsoft.com/l/call/0/0?users=4:${encodeURIComponent(normalized)}`,
      '_blank'
    );
  } else {
    // Native tel: protocol — strip only whitespace for cleaner dialing
    const cleaned = number.replace(/[\s]/g, '');
    window.location.href = `tel:${cleaned}`;
  }
}
